// ── bulk-import-parse: dependency-free CSV/TSV parse + per-row validation ────
// Wave 5 (Bulk participant import). The CLIENT-SIDE preview half of the flow.
//
// ── CONTRACT CHECK #1 (bulk shape) — read from apps/vjal-coaching-api source ──
// There is NO server preview endpoint. `POST /cohorts/:cohort_id/participants/
// bulk` (coaching-shell-session-participant-routes.ts -> bulkCreateCoaching
// Participants) is ALL-OR-NOTHING on the LIVE lane: it throws CO_VALIDATION_FAILED
// on the FIRST duplicate / active-email collision (assertNoDuplicateBulkEmails /
// assertNoActiveParticipantEmails) and returns {participants, count} ONLY when
// every row inserts. There is no per-row partial result from the live backend.
// The richer per-row shape (row_results / imported_count / failed_count /
// "partial_failure") is produced ONLY by the in-memory mock adapter. So the
// preview, the duplicate/invalid/empty/bad-column detection, and the "fix before
// import" gate all live HERE, client-side — we never claim a server preview the
// backend doesn't provide. The confirmed import then POSTs the clean rows; the
// page renders the AUTHORITATIVE backend result (counts/row_results when present,
// else the all-or-nothing count + a thrown CO_VALIDATION_FAILED).
//
// XLSX (.xlsx) is a ZIP container that cannot be parsed without a heavy binary
// dependency this app does not ship. Rather than fabricate XLSX support we DETECT
// it (extension + the PK zip magic) and ask the user to export to CSV first — an
// honest "unsupported format" state, never a fake parse. CSV + TSV (text) parse
// in-browser with no dependency.

import { isValidParticipantEmail } from "./bulk-import-email";

export const BULK_IMPORT_MAX_ROWS = 250; // backend validator cap (1..250).
export const REQUIRED_COLUMNS = ["email"] as const;
export const OPTIONAL_COLUMNS = ["name", "first_name", "last_name", "phone"] as const;
export const KNOWN_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS] as const;

export type ParsedRowStatus = "ready" | "invalid" | "duplicate";

export interface ParsedImportRow {
  /** 1-based row number as it appears to the user (header excluded). */
  row: number;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: ParsedRowStatus;
  /** Human-readable reason when status !== "ready". */
  error: string | null;
}

export interface BulkImportParseResult {
  ok: boolean;
  /** Set when the whole file is unusable (bad type / empty / missing column). */
  fileError: string | null;
  /** Detected delimiter — informational only. */
  delimiter: "," | "\t" | null;
  /** The header columns as parsed (lowercased/trimmed), in file order. */
  headers: string[];
  rows: ParsedImportRow[];
  readyCount: number;
  invalidCount: number;
  duplicateCount: number;
}

const EMPTY_RESULT = (fileError: string): BulkImportParseResult => ({
  ok: false,
  fileError,
  delimiter: null,
  headers: [],
  rows: [],
  readyCount: 0,
  invalidCount: 0,
  duplicateCount: 0,
});

/** True for an .xlsx upload (extension OR the PK\x03\x04 zip magic prefix). */
export function looksLikeXlsx(fileName: string, head: string): boolean {
  if (/\.xlsx$/i.test(fileName)) return true;
  // .xlsx files are ZIP archives: the first bytes are "PK\x03\x04".
  return head.startsWith("PK");
}

/** Pick the delimiter from the header line: prefer a tab, else a comma. */
function detectDelimiter(headerLine: string): "," | "\t" | null {
  if (headerLine.includes("\t")) return "\t";
  if (headerLine.includes(",")) return ",";
  return null;
}

/**
 * Minimal RFC-4180-ish line splitter for ONE record: honors double-quoted fields
 * (so a comma/newline inside quotes stays in the field) and "" escaped quotes.
 * Operates on already newline-split logical records; callers feed whole records.
 */
function splitFields(record: string, delimiter: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < record.length; i += 1) {
    const char = record[i];
    if (inQuotes) {
      if (char === '"') {
        if (record[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      fields.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Split raw text into logical records, keeping quoted newlines together. Returns
 * non-empty trimmed records (a fully blank line is dropped).
 */
function splitRecords(text: string): string[] {
  const records: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
      continue;
    }
    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && text[i + 1] === "\n") i += 1;
      if (current.trim().length > 0) records.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  if (current.trim().length > 0) records.push(current);
  return records;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * Parse + validate raw CSV/TSV text into preview rows. Pure + deterministic so it
 * is unit-testable without a File/FileReader. `existingEmails` is the set of
 * emails already active in the target cohort (lowercased) — a row matching one is
 * flagged `duplicate` client-side (defense-in-depth; the server re-checks).
 */
export function parseBulkImportText(
  rawText: string,
  options: { fileName?: string; existingEmails?: Iterable<string> } = {},
): BulkImportParseResult {
  const head = rawText.slice(0, 8);
  if (options.fileName && looksLikeXlsx(options.fileName, head)) {
    return EMPTY_RESULT(
      "Excel .xlsx files can't be read directly. Export the sheet as CSV (File → Save As → CSV) and upload that.",
    );
  }

  const text = rawText.replace(/^\uFEFF/, ""); // strip a UTF-8 BOM.
  if (text.trim().length === 0) {
    return EMPTY_RESULT("The file is empty. Add a header row and at least one participant.");
  }

  const records = splitRecords(text);
  if (records.length === 0) {
    return EMPTY_RESULT("The file is empty. Add a header row and at least one participant.");
  }

  // A single-column file (just `email`) has no comma/tab — that's still a valid
  // CSV, so default to comma. The missing-column check below is the real guard.
  const delimiter = detectDelimiter(records[0]) ?? ",";

  const headers = splitFields(records[0], delimiter).map(normalizeHeader);
  const missing = REQUIRED_COLUMNS.filter((column) => !headers.includes(column));
  if (missing.length > 0) {
    return EMPTY_RESULT(
      `Missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}. Add a header row with an 'email' column.`,
    );
  }

  const dataRecords = records.slice(1);
  if (dataRecords.length === 0) {
    return EMPTY_RESULT("The file has a header but no participant rows. Add at least one row.");
  }
  if (dataRecords.length > BULK_IMPORT_MAX_ROWS) {
    return EMPTY_RESULT(
      `Too many rows (${dataRecords.length}). Import up to ${BULK_IMPORT_MAX_ROWS} participants at a time.`,
    );
  }

  const columnIndex = (column: string) => headers.indexOf(column);
  const cellOf = (cells: string[], column: string): string => {
    const index = columnIndex(column);
    return index >= 0 ? (cells[index] ?? "").trim() : "";
  };

  const existing = new Set<string>();
  for (const email of options.existingEmails ?? []) existing.add(email.trim().toLowerCase());

  const seenInFile = new Set<string>();
  const rows: ParsedImportRow[] = dataRecords.map((record, index) => {
    const cells = splitFields(record, delimiter);
    const email = cellOf(cells, "email");
    const lowered = email.toLowerCase();
    const name = cellOf(cells, "name");
    const firstName = cellOf(cells, "first_name");
    const lastName = cellOf(cells, "last_name");
    const phone = cellOf(cells, "phone");

    let status: ParsedRowStatus = "ready";
    let error: string | null = null;
    if (!email) {
      status = "invalid";
      error = "Email is required.";
    } else if (!isValidParticipantEmail(email)) {
      status = "invalid";
      error = "Not a valid email address.";
    } else if (seenInFile.has(lowered)) {
      status = "duplicate";
      error = "Duplicate email earlier in this file.";
    } else if (existing.has(lowered)) {
      status = "duplicate";
      error = "Already an active participant in this cohort.";
    }
    if (lowered) seenInFile.add(lowered);

    return {
      row: index + 1,
      email,
      name,
      firstName,
      lastName,
      phone,
      status,
      error,
    };
  });

  const readyCount = rows.filter((row) => row.status === "ready").length;
  const invalidCount = rows.filter((row) => row.status === "invalid").length;
  const duplicateCount = rows.filter((row) => row.status === "duplicate").length;

  return {
    ok: invalidCount === 0 && duplicateCount === 0,
    fileError: null,
    delimiter,
    headers,
    rows,
    readyCount,
    invalidCount,
    duplicateCount,
  };
}

/**
 * The request `rows[]` body for the bulk POST — built ONLY from the ready rows.
 * Mirrors the backend's accepted row keys (email + name/first_name/last_name/
 * phone). Ids are not relevant here (participants are created). camelCase OR
 * snake_case is accepted by the route; we send snake_case to match the existing
 * request bodies.
 */
export function readyRowsToRequest(rows: ParsedImportRow[]): Array<Record<string, unknown>> {
  return rows
    .filter((row) => row.status === "ready")
    .map((row) => {
      const body: Record<string, unknown> = { email: row.email };
      if (row.name) body.name = row.name;
      if (row.firstName) body.first_name = row.firstName;
      if (row.lastName) body.last_name = row.lastName;
      if (row.phone) body.phone = row.phone;
      return body;
    });
}

/** Read a File as text (UTF-8). Thin wrapper so the page stays testable. */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
}
